import React, { useState } from 'react';
import { Dialog, DialogContent } from '@leadsnavi/ui-kit';
import { X, ChevronDown, ChevronUp } from 'lucide-react';
import { Button } from '@leadsnavi/ui-kit';
import { useDialogVisible } from '@/hooks';
import type { IApiInquiryRequestResp } from '@/services/swagger/easourcing/models';
import { TruncatedTextWithTooltip } from '@/components/truncated-text-with-tooltip';

interface InquiryRequestDetailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  data: IApiInquiryRequestResp | null;
  loading?: boolean;
}

/** 表格行：label-value 对 */
const TableRow: React.FC<{
  label: string;
  value?: string | number | null;
  isLast?: boolean;
  maxChars?: number;
}> = ({ label, value, isLast, maxChars = 100 }) => (
  <div className={`flex items-stretch ${!isLast ? 'border-b border-[#E5E5E5]' : ''}`}>
    <div className="w-[150px] shrink-0 px-3 py-2 bg-[#FAFAFA] border-r border-[#E5E5E5] flex items-center">
      <span className="text-xs font-normal text-[#262626] leading-[1.5] tracking-[0.18px] w-full">{label}</span>
    </div>
    <div className="flex-1 px-3 py-2 flex items-center min-w-0">
      <TruncatedTextWithTooltip
        text={value != null ? String(value) : ''}
        maxChars={maxChars}
        className="text-xs font-normal break-words text-[#262626] leading-[1.5] tracking-[0.18px] w-full"
      />
    </div>
  </div>
);

/** 两列行 */
const TableRowDouble: React.FC<{
  label1: string;
  value1?: string | number | null;
  label2: string;
  value2?: string | number | null;
  isLast?: boolean;
}> = ({ label1, value1, label2, value2, isLast }) => (
  <div className={`flex items-stretch ${!isLast ? 'border-b border-[#E5E5E5]' : ''}`}>
    <div className="w-[150px] shrink-0 px-3 py-2 bg-[#FAFAFA] border-r border-[#E5E5E5] flex items-center">
      <span className="text-xs font-normal text-[#262626] leading-[1.5] tracking-[0.18px] w-full">{label1}</span>
    </div>
    <div className="flex-1 px-3 py-2 border-r border-[#E5E5E5] flex items-center min-w-0">
      <TruncatedTextWithTooltip
        text={value1 != null ? String(value1) : ''}
        maxChars={45}
        className="text-xs font-normal break-words text-[#262626] leading-[1.5] tracking-[0.18px] w-full"
      />
    </div>
    <div className="w-[150px] shrink-0 px-3 py-2 bg-[#FAFAFA] border-r border-[#E5E5E5] flex items-center">
      <span className="text-xs font-normal text-[#262626] leading-[1.5] tracking-[0.18px] w-full">{label2}</span>
    </div>
    <div className="flex-1 px-3 py-2 flex items-center min-w-0">
      <TruncatedTextWithTooltip
        text={value2 != null ? String(value2) : ''}
        maxChars={45}
        className="text-xs font-normal break-words text-[#262626] leading-[1.5] tracking-[0.18px] w-full"
      />
    </div>
  </div>
);

/** 折叠式表格区块 */
const CollapsibleSection: React.FC<{
  title: string;
  defaultOpen?: boolean;
  children: React.ReactNode;
}> = ({ title, defaultOpen = true, children }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border border-[#E5E5E5] overflow-hidden rounded-none">
      <button
        type="button"
        className="w-full flex items-center justify-between px-3 py-2 bg-[#EFEFEF] border-b border-[#E5E5E5] text-left"
        onClick={() => setOpen(v => !v)}
      >
        <span className="text-xs font-medium text-[#262626] leading-[1.5] tracking-[0.18px]">{title}</span>
        {open ? (
          <ChevronUp size={18} className="text-[#262626] shrink-0" />
        ) : (
          <ChevronDown size={18} className="text-[#262626] shrink-0" />
        )}
      </button>
      {open && <div className="bg-white">{children}</div>}
    </div>
  );
};

const InquiryRequestDetailDialog: React.FC<InquiryRequestDetailDialogProps> = ({
  open,
  onOpenChange,
  data,
  loading,
}) => {
  const handleClose = () => onOpenChange(false);

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-[700px] w-[700px] gap-0 p-0 border-none" showCloseButton={false}>
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-4 border-b border-[#E5E5E5]">
          <h2 className="text-[20px] font-semibold text-[#171717] leading-[1.2] tracking-[-0.4px]">
            View inquiry details
          </h2>
          <Button variant="ghost" size="sm" onClick={handleClose} className="h-8 w-8 p-0">
            <X className="h-4 w-4 text-[#262626]" />
          </Button>
        </div>

        {/* Content */}
        <div className="px-6 pt-5 pb-[30px] flex flex-col gap-6 overflow-y-auto max-h-[calc(90vh-80px)]">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-sm text-[#737373]">Loading...</div>
          ) : data ? (
            <>
              {/* Product Info */}
              <div className="flex flex-col gap-5">
                <div className="border border-[#E5E5E5] overflow-hidden">
                  {/* Product Info Header */}
                  <div className="px-3 py-2 bg-[#EFEFEF] border-b border-[#E5E5E5]">
                    <span className="text-xs font-medium text-[#262626] leading-[1.5] tracking-[0.18px]">
                      Product Info
                    </span>
                  </div>
                  {/* Row 1: Product Name & Order Quantity */}
                  <TableRowDouble
                    label1="Product Name"
                    value1={data.productName}
                    label2="Order Quantity"
                    value2={data.quantity ? `${data.quantity}${data.unitCode ? ` ${data.unitCode}` : ''}` : undefined}
                  />
                  {/* Row 2: Preferred Unit Price & Max Budget */}
                  <TableRowDouble
                    label1="Preferred Unit Price"
                    value1={data.price ? `${data.currency || ''} ${data.price}`.trim() : undefined}
                    label2="Max Budget"
                    value2={data.maxBudget ? `${data.currency || ''} ${data.maxBudget}`.trim() : undefined}
                  />
                  {/* Row 3: Specification & Model No. */}
                  <TableRowDouble
                    label1="Specification"
                    value1={data.specification || '-'}
                    label2="Model No."
                    value2={data.model}
                  />
                  {/* Row 4: Details/Notes */}
                  <TableRow label="Details" value={data.note} maxChars={200} isLast />
                </div>

                {/* Shipping */}
                <CollapsibleSection title="Shipping">
                  <TableRowDouble
                    label1="Shipping Method"
                    value1={data.shippingMethod}
                    label2="Payment Method"
                    value2={data.payMethod}
                  />
                  <TableRow
                    label="Lead Time"
                    value={data.leadTime != null ? `${data.leadTime} day(s)` : undefined}
                    isLast
                  />
                </CollapsibleSection>

                {/* Buyer Profile */}
                <CollapsibleSection title="Edit Buyer Profile">
                  <TableRowDouble label1="Name" value1={data.userName} label2="Location" value2={data.location} />
                  <TableRowDouble
                    label1="Company Name"
                    value1={data.companyName}
                    label2="Industry"
                    value2={data.industry}
                  />
                  <TableRow label="Company Introduction" value={data.introduction} maxChars={200} isLast />
                </CollapsibleSection>
              </div>
            </>
          ) : (
            <div className="flex items-center justify-center py-10 text-sm text-[#737373]">No data available</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const InquiryRequestDetailDialogV2 = (props: InquiryRequestDetailDialogProps) => {
  const openV2 = useDialogVisible(props.open);
  if (!openV2) return null;
  return <InquiryRequestDetailDialog {...props} />;
};

export default InquiryRequestDetailDialog;
